import { z } from "zod";

/** สัญญา API ระหว่าง web และ services/api (Phase 1) */

export const FREQUENCIES = ["สูงมาก", "สูง", "ปานกลาง", "ต่ำ"] as const;
export const UNITS = ["sqm", "point", "unit", "kg", "cbm"] as const;

export const categorySchema = z.object({
  category_id: z.string(),
  name_th: z.string(),
  name_en: z.string(),
  sub_categories_th: z.string(),
  frequency: z.enum(FREQUENCIES),
  unit: z.enum(UNITS),
  unit_label_th: z.string(),
  labor_min_thb: z.number(),
  labor_max_thb: z.number(),
  material_min_thb: z.number(),
  material_max_thb: z.number(),
  display_order: z.number().int(),
  notes: z.string().optional(),
  price_source_id: z.string(),
  price_version: z.number().int(),
  material_components: z
    .array(z.object({ item: z.string(), spec: z.string().optional(), qty_per_unit: z.string(), unit: z.string(), note: z.string().optional() }))
    .default([]),
});
export type Category = z.infer<typeof categorySchema>;

/** GET /categories */
export interface CategoriesResponse {
  items: Category[];
}

/** GET /me (ต้องมี Authorization: Bearer <accessToken>) */
export interface MeResponse {
  userId: string;
  email: string;
  name?: string;
  plan: "free" | "basic" | "standard" | "premium";
  consentAcceptedAt?: string;
  /** เวอร์ชันนโยบายที่ผู้ใช้ยินยอมไว้ (ต้องตรงกับ requiredConsentVersion ถึงใช้ AI/อัปโหลดได้) */
  consentVersion?: string;
  /** เวอร์ชันที่ต้องยินยอมในตอนนี้ (server เป็นผู้กำหนด) */
  requiredConsentVersion?: string;
  /** อยู่ใน Cognito group admin (แสดงเมนู Admin; สิทธิ์จริงตรวจฝั่ง server ทุก request) */
  isAdmin?: boolean;
  /** วันหมดอายุแพ็กเกจเสียเงิน (ISO) ถ้ามี */
  planExpiresAt?: string;
}

/** POST /auth/refresh (cookie httpOnly + header x-workcost-csrf: 1) */
export interface RefreshResponse {
  accessToken: string;
  expiresIn: number;
}

/**
 * GET /auth/login?provider=Google|email&returnTo=/path
 * -> 302 ไป Cognito authorize (PKCE ที่ BFF) ; GET /auth/callback -> ตั้ง cookie แล้ว 302 กลับ web returnTo
 * POST /auth/logout -> ลบ cookie
 */
export const AUTH_PROVIDERS = ["Google", "email"] as const;

export const UPLOAD_TYPES = ["image/jpeg", "image/png", "image/webp"] as const;
export const MAX_PHOTOS = 3;
export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

export const presignRequestSchema = z.object({
  files: z.array(z.object({ contentType: z.enum(UPLOAD_TYPES), size: z.number().int().positive().max(MAX_PHOTO_BYTES) })).min(1).max(MAX_PHOTOS),
});
export type PresignRequest = z.infer<typeof presignRequestSchema>;

/** POST /uploads/presign -> PUT ไฟล์ไป url พร้อม headers ที่ระบุ */
export interface PresignResponse {
  uploads: { key: string; url: string; headers: Record<string, string>; expiresIn: number }[];
}

/** POST /uploads/verify {keys} -> server ตรวจ magic bytes หลังอัปโหลด */
export interface VerifyResponse {
  results: { key: string; ok: boolean; reason?: "bad_type" | "too_large" | "too_small" | "missing" }[];
}
